// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Content/InternetHeaderView.js
import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import ToastNotification from '../ToastNotification';
import { v4 as uuidv4 } from 'uuid';
import '../../css/InternetHeaderView.scss';
import { fetchPromotions } from '../../utils/ExploreInternetHandlers';

const InternetHeaderView = ({
  headers,
  setHeaders,
  totalCount,
  searchQuery,
  pageLength,
  currentPage,
  setCurrentPage,
  handlePageLengthChange,
  handleInsertHeader,
  handleUpdateHeader,
  handleDeleteHeader,
  refreshData,
}) => {
  const [editingRows, setEditingRows] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [allPromotions, setAllPromotions] = useState([]);

  const first = (currentPage - 1) * pageLength;

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        const data = await fetchPromotions();
        setAllPromotions(data);
      } catch (err) {
        console.error('Error fetching promotions for indexes:', err);
      }
    };
    loadPromotions();
  }, []);

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const refresh = () => {
    if (shouldRefresh) {
      if (typeof refreshData === 'function') {
        refreshData();
      } else {
        console.warn('No refreshData provided');
        setHeaders((prev) => [...prev]);
      }
      setShouldRefresh(false);
    }
  };

  const indexesBodyTemplate = (rowData) => {
    if (!rowData?.indexes || !Array.isArray(rowData.indexes)) return '';
    const nameList = rowData.indexes.map((id) => {
      const found = allPromotions.find((p) => p.id === id);
      return found ? found.title : `ID:${id}`;
    });
    return nameList.join(', ');
  };

  const textEditor = (options) => (
    <InputText
      type="text"
      value={options.value || ''}
      onChange={(e) => options.editorCallback(e.target.value)}
      style={{ width: '100%' }}
    />
  );

  const multiSelectIndexesEditor = (options) => {
    const msOptions = allPromotions.map((p) => ({
      label: p.title,
      value: p.id,
    }));

    return (
      <MultiSelect
        value={options.value || []}
        options={msOptions}
        onChange={(e) => options.editorCallback(e.value)}
        placeholder="Select indexes"
        display="chip"
        className="w-full"
      />
    );
  };

  const onRowEditComplete = async (e) => {
    const { newData } = e;
    setEditingRows({});
    if (!newData || !newData.filterKey) return;

    try {
      if (!newData.filterKey || !newData.header) {
        throw new Error('filterKey and header are required');
      }
      await handleUpdateHeader(newData);
      setHeaders((prev) => [...prev]);
      showToastMessage(`Header ${newData.filterKey} updated successfully`, 'success');
    } catch (err) {
      console.error('Error updating header:', err);
      showToastMessage(`Error: ${err.message}`, 'error');
      setShouldRefresh(true);
    } finally {
      refresh();
    }
  };

  const deleteActionTemplate = (rowData) => (
    <Button
      icon="pi pi-trash"
      className="p-button-danger p-button-sm"
      onClick={async () => {
        try {
          await handleDeleteHeader(rowData.filterKey);
          setHeaders((prev) => prev.filter((h) => h.filterKey !== rowData.filterKey));
          showToastMessage('Header deleted successfully', 'success');
        } catch (err) {
          console.error('Error deleting header:', err);
          showToastMessage(`Error: ${err.message}`, 'error');
          setShouldRefresh(true);
        } finally {
          refresh();
        }
      }}
    />
  );

  const addHeader = async () => {
    const newHeader = {
      filterKey: uuidv4().slice(0, 5).toUpperCase(),
      header: 'New AIS Header',
      indexes: [],
    };

    try {
      if (!newHeader.filterKey || !newHeader.header) {
        throw new Error('filterKey and header are required');
      }
      const response = await handleInsertHeader(newHeader);
      if (Array.isArray(response) && response.length > 0) {
        const realKey = response[0].filterKey || newHeader.filterKey;
        const insertedHeader = { ...newHeader, filterKey: realKey };
        setHeaders((prev) => [...prev, insertedHeader]);
        showToastMessage('Header added successfully', 'success');
      } else {
        throw new Error('No filterKey returned from server');
      }
    } catch (err) {
      console.error('Error adding header:', err);
      showToastMessage(`Error: ${err.message}`, 'error');
      setShouldRefresh(true);
    } finally {
      refresh();
    }
  };

  const filteredHeaders = headers.filter(
    (h) =>
      h &&
      ((h.header || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (h.filterKey || '').toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />

      <div className="internet-header-wrapper">
        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <div className="select-container me-md-2">
            <select
              id="pageLengthSelect"
              className="form-select"
              value={pageLength}
              onChange={handlePageLengthChange}
            >
              <option value="10">10 Rows</option>
              <option value="20">20 Rows</option>
              <option value="50">50 Rows</option>
            </select>
          </div>
        </div>

        <DataTable
          value={filteredHeaders}
          paginator
          rows={pageLength}
          totalRecords={totalCount}
          first={first}
          editMode="row"
          dataKey="filterKey"
          onRowEditComplete={onRowEditComplete}
          className="p-datatable-gridlines"
        >
          <Column
            field="filterKey"
            header="Filter Key"
            sortable
            style={{ width: '120px' }}
          />
          <Column
            field="header"
            header="Header"
            sortable
            editor={textEditor}
          />
          <Column
            field="indexes"
            header="Indexes"
            style={{ minWidth: '200px' }}
            body={indexesBodyTemplate}
            editor={multiSelectIndexesEditor}
            className="indexes-column" // Added custom class for SCSS targeting
          />
          <Column
            rowEditor
            headerStyle={{ width: '10%', minWidth: '8rem' }}
            bodyStyle={{ textAlign: 'center' }}
          />
          <Column
            header="Delete"
            body={deleteActionTemplate}
            headerStyle={{ width: '10%', minWidth: '8rem' }}
            bodyStyle={{ textAlign: 'center' }}
          />
        </DataTable>

        <div className="text-right mt-2">
          <Button
            icon="pi pi-plus"
            label="Add Header"
            className="p-button-success p-button-sm"
            onClick={addHeader}
          />
        </div>
      </div>
    </>
  );
};

export default InternetHeaderView;